// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-unused-expressions, @typescript-eslint/require-await, react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

import React, { FC, useEffect, useState } from "react";
import { Checkbox, Icon, Loader, Popup, Segment, Table } from "semantic-ui-react";
import * as tableShader from "../../../Lumber/TableShader";
import * as valuePrecisionConverter from "../../../Lumber/ValuePrecisionConverter";
import * as protocolCalculations from "./ProtocolCalculations";

import _ from "lodash";

import type {
	DataNormalizationCompiledRow,
	MeanComparisonLettersByAssessment,
	ProtocolDataNormalizationTableProps,
} from "./types";

const ProtocolDataNormalizationTable: FC<ProtocolDataNormalizationTableProps> = ({
	compiledData,
	tukeyData,
	dataNormalization,
	updateCheckedTreatment,
	treatmentsChecked,
	disableCheckBoxes,
	selectedAssessmentOption,
	hideLetters,
	needToRerunStats,
	needToRerunNormalization,
	// TODO(types): allMeanComparisonData and uniqueAnalysesCount are never passed by any parent, so the
	// `.length` comparisons below are always undefined === undefined and the checkboxes stay enabled.
	allMeanComparisonData,
	uniqueAnalysesCount,
	hideUncheckedTrialData = false,
	oneTrialDatasetError = false,
	multiTrialDatasetError = false,
	collapseView = false,
}) => {
	const [tableData, setTableData] = useState<DataNormalizationCompiledRow[] | null>(null);
	const [tukeyTableData, setTukeyTableData] = useState<MeanComparisonLettersByAssessment | null>(null);
	const [toggleAll, setToggleAll] = useState(true);
	const [tukeyDataTreatmentList, setTukeyDataTreatmentList] = useState<number[]>([]);

	// TODO(types): selectedAssessmentOption can be null, in which case the lookups below read the
	// "null" key.
	const assessmentKey = String(selectedAssessmentOption);

	useEffect(() => {
		if (tukeyData) {
			setTukeyTableData(tukeyData);
			//Get list of treatments checked treatments that have data. Will use this list to set "N/A" to unchecked treatements.
			//This applies when unchecking treatments and getting new data
			const list = _.uniqBy(tukeyData[assessmentKey], "trialTreatmentId");
			setTukeyDataTreatmentList(
				_.map(list, (treatment) => {
					return treatment.trialTreatmentId;
				}),
			);
		}
	}, [tukeyData]);

	useEffect(() => {
		if (compiledData) {
			//Filter unchecked treatements and don't use them to re-color. This applies when unchecking treatments and getting new data
			const tempCompiledData = _.filter(compiledData, (cd) => !cd.hasMissingTreatments);
			let filteredCompiledData = _.filter(tempCompiledData, (cd) => {
				return tukeyDataTreatmentList.includes(cd.trialTreatmentId);
			});
			filteredCompiledData = _.isEmpty(filteredCompiledData) ? tempCompiledData : filteredCompiledData;

			filteredCompiledData[0]?.reversed === true || dataNormalization === "RANK ORDER"
				? tableShader.calculateShadingForTreatmentReversed(filteredCompiledData, "average", "236, 196, 76")
				: tableShader.calculateShadingForTreatment(filteredCompiledData, "average", "236, 196, 76");
			setTableData(compiledData);
		}
	}, [compiledData]);

	async function updateCheckAll() {
		const toggleValue = toggleAll;
		setToggleAll(!toggleValue);
		return;
	}

	function toggleCheckAll() {
		updateCheckedTreatment(!toggleAll, _.map(treatmentsChecked, "trialTreatmentId"));
	}

	return (
		<Segment basic style={{ overflowX: "auto", padding: 0 }}>
			<Table celled striped selectable color="blue" textAlign="center" collapsing>
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell
							style={{ zIndex: "0", cursor: "pointer" }}
							onClick={() => {
								if (!disableCheckBoxes && allMeanComparisonData?.length === uniqueAnalysesCount?.length) {
									// TODO(types): toggleCheckAll runs as soon as updateCheckAll() starts, not when it settles.
									const pendingCheckAll = updateCheckAll();
									toggleCheckAll();
									void pendingCheckAll;
								}
							}}
						>
							<div style={{ display: "flex", gap: 5 }}>
								<Checkbox
									checked={toggleAll}
									disabled={disableCheckBoxes || allMeanComparisonData?.length !== uniqueAnalysesCount?.length}
								/>{" "}
								Check All
							</div>
						</Table.HeaderCell>
						<Table.HeaderCell style={{ zIndex: "0" }}>#</Table.HeaderCell>
						<Table.HeaderCell style={{ zIndex: "0" }}>Type</Table.HeaderCell>
						<Table.HeaderCell style={{ zIndex: "0" }}>Name</Table.HeaderCell>
						<Table.HeaderCell style={{ zIndex: "0" }}>{dataNormalization}</Table.HeaderCell>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{_.reduce(
						tableData,
						(acc, treatment) => {
							const checked = _.find(
								treatmentsChecked,
								(tc) => tc.trialTreatmentId === treatment.trialTreatmentId,
							)?.treatmentChecked;

							if (!collapseView || checked) {
								acc.push(
									<Table.Row key={treatment.trialTreatmentId}>
										<Table.Cell>
											<Checkbox
												checked={checked}
												disabled={disableCheckBoxes || allMeanComparisonData?.length !== uniqueAnalysesCount?.length}
												onChange={() => {
													updateCheckedTreatment(!checked, treatment.trialTreatmentId);
												}}
											/>
										</Table.Cell>
										<Table.Cell>{treatment.trialTreatmentId}</Table.Cell>
										<Table.Cell>{treatment.treatmentTypeName}</Table.Cell>
										<Table.Cell
											style={{
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
											textAlign="left"
										>
											{/* React omits a null title the same as undefined. */}
											<span title={treatment.treatmentName ?? undefined}>{treatment.treatmentName}</span>
											{treatment.hasMissingTreatments ? (
												<span style={{ float: "right" }}>
													<Popup
														trigger={<Icon name="info circle" />}
														content="This treatment is missing from some of the trials on this protocol."
													/>
												</span>
											) : null}
										</Table.Cell>
										<Table.Cell
											style={{
												backgroundColor: needToRerunNormalization
													? ""
													: treatment.treatmentChecked
														? treatment.color
														: "lightGray",
												width: "150px",
												position: "sticky",
											}}
										>
											<div style={{ display: "flex", gap: "2px", justifyContent: "space-between" }}>
												<span>
													{needToRerunNormalization
														? ""
														: treatment.hasMissingTreatments
															? "N/A"
															: treatment.average && treatment.treatmentChecked
																? valuePrecisionConverter.getPrecision(treatment.average) +
																	"" +
																	(dataNormalization.includes("%") ? " %" : "")
																: !treatment.treatmentChecked
																	? ""
																	: "N/A"}
												</span>
												<span>
													{disableCheckBoxes ? (
														<Loader active size="tiny" />
													) : !treatment.treatmentChecked ||
													  hideLetters ||
													  needToRerunStats ||
													  needToRerunNormalization ||
													  hideUncheckedTrialData ? (
														""
													) : (
														_.find(
															// TODO(types): tukeyTableData is null until the first tukeyData arrives; this would
															// throw if letters were shown before then. Cast keeps the unguarded read.
															(tukeyTableData as MeanComparisonLettersByAssessment)[assessmentKey],
															(td) => td.trialTreatmentId === treatment.trialTreatmentId,
														)?.tukeyLetters
													)}
												</span>
											</div>
										</Table.Cell>
									</Table.Row>,
								);
							}

							return acc;
						},
						[] as React.ReactNode[],
					)}
				</Table.Body>
				<Table.Body color="blue">
					<Table.Row>
						<Table.Cell textAlign="right" colSpan={4}>
							CV
						</Table.Cell>
						<Table.Cell>
							{(_.some(tableData, (td) => !td?.treatmentChecked) && hideLetters) ||
							hideLetters ||
							needToRerunNormalization ||
							disableCheckBoxes ||
							hideUncheckedTrialData
								? ""
								: multiTrialDatasetError || oneTrialDatasetError
									? "N/A"
									: protocolCalculations.calculateStandardDeviationForCompiledTable(
											_.filter(tableData, (td) => td?.treatmentChecked),
										)}
						</Table.Cell>
					</Table.Row>
					<Table.Row>
						<Table.Cell textAlign="right" colSpan={4}>
							P-Value
						</Table.Cell>
						{(_.some(tableData, (td) => !td.treatmentChecked) && hideLetters) ||
						hideLetters ||
						needToRerunStats ||
						needToRerunNormalization ||
						disableCheckBoxes ||
						hideUncheckedTrialData ? (
							<Table.Cell></Table.Cell>
						) : tukeyTableData && tukeyTableData[assessmentKey]?.length > 0 ? (
							<Table.Cell>
								{Number(Number(tukeyTableData[assessmentKey][0]?.pValue).toFixed(5)) === 0
									? "<0.00001"
									: Number(Number(tukeyTableData[assessmentKey][0]?.pValue).toFixed(5))}
							</Table.Cell>
						) : (
							<Table.Cell>N/A</Table.Cell>
						)}
					</Table.Row>
				</Table.Body>
			</Table>
		</Segment>
	);
};

export default ProtocolDataNormalizationTable;
